import React, { useState, useCallback, useMemo } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, where, writeBatch, doc } from 'firebase/firestore';
import { Container, Card, Button, Spinner, Alert, ListGroup, ButtonGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { FileEarmarkArrowUp, CheckCircleFill, XCircleFill } from 'react-bootstrap-icons';
import { convert } from 'encoding-japanese';

function CsvImport() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadResult, setUploadResult] = useState(null);
  const [importFormat, setImportFormat] = useState('yupri'); // 'yupri' or 'istar'

  const acceptedFileTypes = useMemo(() => {
    if (importFormat === 'yupri') {
      return { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] };
    }
    return { 'text/csv': ['.csv'] };
  }, [importFormat]);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length === 0) {
      const allowedExt = importFormat === 'yupri' ? '.xlsx' : '.csv';
      setError(`許可されていないファイル形式です。${allowedExt} ファイルを選択してください。`);
      return;
    }
    const file = acceptedFiles[0];
    handleFileUpload(file);
  }, [importFormat]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
    multiple: false,
  });

  const processData = async (data) => {
    if (data.length === 0) {
        setError('ファイルにデータが含まれていません。');
        setLoading(false);
        return;
    }
    
    let requiredColumns, customerOrderNumberCol, trackingNumberCol;
    if (importFormat === 'yupri') {
        requiredColumns = ['お客様側管理番号', 'お問い合わせ番号'];
        customerOrderNumberCol = 'お客様側管理番号';
        trackingNumberCol = 'お問い合わせ番号';
    } else { // istar
        requiredColumns = ['お客様管理番号', '送り状番号'];
        customerOrderNumberCol = 'お客様管理番号';
        trackingNumberCol = '送り状番号';
    }

    const actualColumns = Object.keys(data[0]);
    const missingColumns = requiredColumns.filter(col => !actualColumns.includes(col));

    if (missingColumns.length > 0) {
        setError(`ファイルに必要な列が見つかりません: ${missingColumns.join(', ')}。ファイルの形式が正しいか、文字コードがShift-JISであることを確認してください。`);
        setLoading(false);
        return;
    }
    
    try {
        const ordersRef = collection(db, 'orders');
        const q = query(ordersRef, where('status', '==', 'processed'));
        const querySnapshot = await getDocs(q);

        const ordersToUpdate = new Map();
        querySnapshot.forEach(doc => {
            ordersToUpdate.set(doc.id, { id: doc.id, ...doc.data() });
        });

        const batch = writeBatch(db);
        let updatedCount = 0;
        const notFound = [];
        const alreadyShipped = [];

        for (const row of data) {
            const customerOrderNumber = row[customerOrderNumberCol];
            const trackingNumber = row[trackingNumberCol];

            if (!customerOrderNumber || !trackingNumber) continue;

            let foundOrder = null;
            let orderDocId = null;

            for (const [docId, order] of ordersToUpdate.entries()) {
                const destIndex = order.shippingDestinations.findIndex(d => d.orderNumber === String(customerOrderNumber));
                if (destIndex !== -1) {
                    foundOrder = order;
                    orderDocId = docId;
                    break;
                }
            }

            if (foundOrder) {
                const destIndex = foundOrder.shippingDestinations.findIndex(d => d.orderNumber === String(customerOrderNumber));
                if (foundOrder.shippingDestinations[destIndex].trackingNumber) {
                    alreadyShipped.push(customerOrderNumber);
                    continue;
                }

                foundOrder.shippingDestinations[destIndex].trackingNumber = String(trackingNumber);

                const allShipped = foundOrder.shippingDestinations.every(d => d.trackingNumber);
                if (allShipped) {
                    foundOrder.status = 'shipped';
                }

                const orderRef = doc(db, 'orders', orderDocId);
                batch.update(orderRef, {
                    shippingDestinations: foundOrder.shippingDestinations,
                    status: foundOrder.status,
                    shippingCarrier: importFormat,
                });
                updatedCount++;
            } else {
                notFound.push(customerOrderNumber);
            }
        }

        await batch.commit();
        setUploadResult({ success: updatedCount, notFound, alreadyShipped });

    } catch (err) {
        setError('データベースの更新中にエラーが発生しました。');
        console.error(err);
    }
    setLoading(false);
  };

  const handleFileUpload = (file) => {
    setLoading(true);
    setError('');
    setUploadResult(null);

    const reader = new FileReader();

    if (importFormat === 'yupri') {
        reader.onload = (e) => {
            try {
                const buffer = e.target.result;
                const workbook = XLSX.read(buffer, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);
                processData(jsonData);
            } catch (err) {
                setError('XLSXファイルの解析に失敗しました。ファイルが破損しているか、形式が正しくありません。');
                setLoading(false);
                console.error(err);
            }
        };
        reader.readAsArrayBuffer(file);
    } else {
        reader.onload = (e) => {
            const buffer = e.target.result;
            const fromEncoding = 'SJIS';
            const text = convert(new Uint8Array(buffer), {
                to: 'UNICODE',
                from: fromEncoding,
                type: 'string'
            });

            // ★★★【修正】ここからが今回の修正箇所 ★★★
            Papa.parse(text, {
                header: true,
                skipEmptyLines: true,
                transformHeader: (header) => header.trim(),
                transform: (value) => {
                    if (typeof value === 'string' && value.startsWith("'")) {
                        return value.substring(1);
                    }
                    return value;
                },
                complete: (results) => {
                    // 過剰な文字コードチェックを削除し、processDataに直接渡す
                    processData(results.data);
                },
                error: (err) => {
                    setError('CSVファイルの解析に失敗しました。');
                    console.error(err);
                    setLoading(false);
                }
            });
            // ★★★ ここまでが今回の修正箇所 ★★★
        };
        reader.readAsArrayBuffer(file);
    }
  };

  return (
    <main>
      <Container className="my-4">
        <Card className="shadow-sm">
          <Card.Header as="h2" className="p-3">出荷情報データ取込</Card.Header>
          <Card.Body className="p-4">
            <Alert variant="info">
              <Alert.Heading>処理内容</Alert.Heading>
              <p>
                各配送業者のシステムから出力された、お問い合わせ番号（追跡番号）を含むファイルをアップロードしてください。
                <br />
                ファイルの内容を元に、システム内の注文データが自動で更新されます。
              </p>
              <hr />
              <p className="mb-0">
                1. 注文のステータスが「受付済み」から**「出荷済み」**に更新されます。
                <br />
                2. 各送付先に**お問い合わせ番号**と**配送業者**が登録されます。
              </p>
            </Alert>

            <div className="text-center mb-3">
              <p className="mb-2"><strong>1. 取り込むファイルの形式を選択してください</strong></p>
              <ButtonGroup>
                <Button variant={importFormat === 'yupri' ? 'primary' : 'outline-primary'} onClick={() => setImportFormat('yupri')}>ゆうプリR (xlsx)</Button>
                <Button variant={importFormat === 'istar' ? 'primary' : 'outline-primary'} onClick={() => setImportFormat('istar')}>福山通運 (csv)</Button>
              </ButtonGroup>
            </div>

            <div
              {...getRootProps()}
              className={`p-5 border-2 border-dashed rounded text-center ${isDragActive ? 'border-primary bg-primary-subtle' : 'border-secondary'}`}
              style={{ borderStyle: 'dashed', cursor: 'pointer' }}
            >
              <input {...getInputProps()} />
              <FileEarmarkArrowUp size={48} className="mb-3 text-secondary" />
              <p className="mb-1"><strong>2. ここに{importFormat === 'yupri' ? 'XLSX' : 'CSV'}ファイルをドロップ</strong></p>
              <small className="text-muted">またはクリックしてファイルを選択</small>
            </div>

            {loading && (
              <div className="text-center mt-4">
                <Spinner animation="border" /><p>処理中です...</p>
              </div>
            )}
            {error && <Alert variant="danger" className="mt-4">{error}</Alert>}
            {uploadResult && (
                <Alert variant="success" className="mt-4">
                  <Alert.Heading><CheckCircleFill className="me-2" />処理が完了しました</Alert.Heading>
                  <p><strong>{uploadResult.success}件</strong>の送付先情報にお問い合わせ番号を登録し、ステータスを更新しました。</p>
                  {uploadResult.notFound.length > 0 && (
                      <>
                        <hr />
                        <p className="mb-1"><strong><XCircleFill className="me-2 text-danger" />以下の注文番号は見つかりませんでした:</strong></p>
                        <ListGroup variant="flush" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                          {uploadResult.notFound.map(num => <ListGroup.Item key={num} className="bg-transparent text-danger py-1">{num}</ListGroup.Item>)}
                        </ListGroup>
                      </>
                  )}
                  {uploadResult.alreadyShipped.length > 0 && (
                      <>
                        <hr />
                        <p className="mb-1"><strong><XCircleFill className="me-2 text-warning" />以下の注文は既に出荷済みでした:</strong></p>
                        <ListGroup variant="flush" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                          {uploadResult.alreadyShipped.map(num => <ListGroup.Item key={num} className="bg-transparent text-warning py-1">{num}</ListGroup.Item>)}
                        </ListGroup>
                      </>
                  )}
                </Alert>
            )}

            <hr className="my-4" />
            <Link to="/shipper-menu">出荷者メニューに戻る</Link>
          </Card.Body>
        </Card>
      </Container>
    </main>
  );
}

export default CsvImport;

